import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { CustomLegend } from "modules/LocationReports/components";
import "modules/LocationReports/components/StackedBarChart/StackedBarChart.scss";
import { getTranslationOrValue } from "modules/LocationReports/utils";
import { REPORTS_COLORS, extractAllNameFields } from "utils/common";

import type { ReportColor } from "models";

type StackedBarChartProps = {
  data: Record<string, string>[];
  unit: string;
  colors: ReportColor[];
};

export const StackedBarChart = ({ data, colors, unit }: StackedBarChartProps) => {
  const { t } = useTranslation();

  return (
    <div className="StackedBarChart">
      <ResponsiveContainer width="100%" height={450} className="margin-auto">
        <BarChart data={data} margin={{ top: 20, right: 30, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis unit={unit} domain={unit === "%" ? [0, 100] : ["auto", "auto"]} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;

              return (
                <div className="tooltip-wrapper">
                  <p className="tooltip-label">{label}</p>
                  {payload.map((entry, index) => (
                    <ul key={index} className="tooltip-item-list">
                      <p style={{ color: entry.color }}>{`${getTranslationOrValue(t, String(entry.name))}: ${entry.value}`}</p>
                    </ul>
                  ))}
                </div>
              );
            }}
          />
          <Legend content={<CustomLegend />} />

          {extractAllNameFields(data)
            .filter(k => k !== "name")
            .map((k, i) => {
              const item = colors.find(col => col.name === k);
              const color = item?.color || null;

              let stack = "stack_a";
              let name = k;

              if (k.startsWith("stack_a.")) {
                name = k.replace("stack_a.", "");
              } else if (k.startsWith("stack_b.")) {
                stack = "stack_b";
                name = k.replace("stack_b.", "");
              }

              return (
                <Bar stackId={stack} name={name} key={i} dataKey={k} fill={color || REPORTS_COLORS[i % REPORTS_COLORS.length]} unit={unit} isAnimationActive={false} />
              );
            })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
